import React, { useState } from "react";
import { X, Save, ArrowUp, ArrowDown, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";

import { ImageUploadField } from "@/pages/admin/fields/ImageUploadField";
import { Section, TextField, IconBtn } from "@/pages/admin/fields/primitives";

// -------- Software Editor Drawer (Phase Software Catalog Redesign) --------
// Every field on the SoftwareModel is editable here except `id` /
// `display_order` (reorder is handled by drag-drop in the list view).
// Option groups are the biggest addition — dynamic per product (spec:
// "flexible per product, not hardcoded to one shape").

const newOption = () => ({
  id: `swopt_${Math.random().toString(36).slice(2, 10)}`,
  label: "",
  price_delta: 0,
  badge: "",
  description: "",
  best_for: "",
});
const newGroup = () => ({
  id: `swgrp_${Math.random().toString(36).slice(2, 10)}`,
  label: "",
  hint: "",
  options: [newOption()],
});

export const SoftwareEditorDrawer = ({ item, onClose, onSave }) => {
  const [form, setForm] = useState(() => ({
    id: item?.id,
    name: "",
    slug: "",
    publisher: "",
    category: "creative",
    status: "active",
    sales_mode: "request_quote",
    price: 0,
    price_label: "",
    price_currency: "AWG",
    tagline: "",
    description_short: "",
    description_long: "",
    subtitle: "",
    logo: "",
    vendor_url: "",
    install_guide: "",
    availability: "available",
    option_groups: [],
    addon_services: [],
    best_for: [],
    highlights_heading: "",
    highlights: [],
    categories: [],
    display_order: 0,
    ...(item || {}),
  }));
  // Ensure `categories` is always a normalised array — a legacy row with
  // only the singular `category` string is upgraded on first edit so
  // saving from the admin lands the multi-tag shape.
  if (!Array.isArray(form.categories) || form.categories.length === 0) {
    if (form.category) form.categories = [form.category];
    else form.categories = [];
  }

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    const numeric = k === "price" || k === "display_order";
    setForm((f) => ({ ...f, [k]: numeric ? Number(v || 0) : v }));
  };

  // ---- Option group helpers ----
  const setGroup = (gi, patch) =>
    setForm((f) => ({
      ...f,
      option_groups: f.option_groups.map((g, i) => (i === gi ? { ...g, ...patch } : g)),
    }));
  const setOption = (gi, oi, patch) =>
    setForm((f) => ({
      ...f,
      option_groups: f.option_groups.map((g, i) =>
        i === gi
          ? { ...g, options: g.options.map((o, j) => (j === oi ? { ...o, ...patch } : o)) }
          : g,
      ),
    }));
  const addGroup = () =>
    setForm((f) => ({ ...f, option_groups: [...f.option_groups, newGroup()] }));
  const removeGroup = (gi) =>
    setForm((f) => ({
      ...f,
      option_groups: f.option_groups.filter((_, i) => i !== gi),
    }));
  const moveGroup = (gi, dir) =>
    setForm((f) => {
      const arr = [...f.option_groups];
      const j = gi + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[gi], arr[j]] = [arr[j], arr[gi]];
      return { ...f, option_groups: arr };
    });
  const addOption = (gi) =>
    setForm((f) => ({
      ...f,
      option_groups: f.option_groups.map((g, i) =>
        i === gi ? { ...g, options: [...g.options, newOption()] } : g,
      ),
    }));
  const removeOption = (gi, oi) =>
    setForm((f) => ({
      ...f,
      option_groups: f.option_groups.map((g, i) =>
        i === gi ? { ...g, options: g.options.filter((_, j) => j !== oi) } : g,
      ),
    }));

  // ---- Add-on service links ----
  const updateAddon = (i, patch) =>
    setForm((f) => ({
      ...f,
      addon_services: f.addon_services.map((a, idx) =>
        idx === i ? { ...a, ...patch } : a,
      ),
    }));
  const addAddon = () =>
    setForm((f) => ({
      ...f,
      addon_services: [...f.addon_services, { label: "", url: "" }],
    }));
  const removeAddon = (i) =>
    setForm((f) => ({
      ...f,
      addon_services: f.addon_services.filter((_, idx) => idx !== i),
    }));

  const commit = () => {
    if (!form.name?.trim()) {
      toast.error("Product name is required.");
      return;
    }
    if (!form.slug?.trim()) {
      toast.error("Slug is required (used in the /software/[slug] URL).");
      return;
    }
    onSave(form);
  };

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/60 grid place-items-end"
      onClick={onClose}
    >
      <aside
        data-testid="software-editor-drawer"
        className="bg-[#0F0F0F] w-full max-w-2xl h-full border-l border-[#262626] p-6 overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-6 sticky top-0 -mt-6 -mx-6 px-6 py-4 bg-[#0F0F0F] border-b border-[#262626] z-10">
          <div>
            <div className="ur-eyebrow">Software Product</div>
            <h3 className="font-display text-xl font-bold mt-1">
              {item?.id ? `Edit · ${item.name || form.name || "Product"}` : "Create Software Product"}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="software-save"
              onClick={commit}
              className="ur-btn-primary text-sm py-2.5 px-4"
            >
              Save <Save className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="w-9 h-9 rounded-full border border-[#262626] grid place-items-center"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Basics */}
        <Section
          title="Basics"
          destination="Renders on: /software card + /software/[slug] hero"
          hint="Core identity — name, slug, publisher, category. Category drives the filter chips on the /software index."
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="Name" value={form.name} onChange={set("name")} placeholder="Adobe Creative Cloud" />
            <TextField label="Slug" value={form.slug} onChange={set("slug")} placeholder="adobe-creative-cloud" hint="Used in URL: /software/{slug}" />
            <TextField label="Publisher" value={form.publisher} onChange={set("publisher")} placeholder="Adobe / Microsoft / Autodesk" />
            <div className="sm:col-span-1">
              <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">
                Categories (tags)
              </span>
              <div
                className="mt-1 flex flex-wrap gap-2"
                data-testid="software-categories-editor"
              >
                {[
                  { key: "creative", label: "Creative" },
                  { key: "architecture", label: "Architecture & 3D" },
                  { key: "business", label: "Business" },
                  { key: "ai", label: "AI" },
                ].map((opt) => {
                  const active = (form.categories || []).includes(opt.key);
                  return (
                    <button
                      type="button"
                      key={opt.key}
                      data-testid={`software-category-tag-${opt.key}`}
                      onClick={() =>
                        setForm((f) => {
                          const has = (f.categories || []).includes(opt.key);
                          const next = has
                            ? f.categories.filter((c) => c !== opt.key)
                            : [...(f.categories || []), opt.key];
                          // Keep the legacy singular in sync with the first
                          // multi-tag so old consumers still work.
                          return { ...f, categories: next, category: next[0] || "" };
                        })
                      }
                      className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest transition-all ${
                        active
                          ? "bg-[#FF6600] text-black"
                          : "bg-[#1A1A1A] text-[#A3A3A3] border border-[#262626] hover:border-[#FF6600]/40"
                      }`}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <span className="mt-1 block text-[11px] text-[#7A7A7A]">
                Multi-select. A product with no tags won't be filterable —
                pick at least one.
              </span>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Status</span>
              <select
                value={form.status}
                onChange={set("status")}
                className="mt-1 w-full bg-[#141414] border border-[#262626] rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF6600] outline-none"
                data-testid="software-status-select"
              >
                <option value="active">Active (visible to customers)</option>
                <option value="hidden">Hidden (soft-delete — keeps history)</option>
              </select>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Availability</span>
              <select
                value={form.availability}
                onChange={set("availability")}
                className="mt-1 w-full bg-[#141414] border border-[#262626] rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF6600] outline-none"
                data-testid="software-availability-select"
              >
                <option value="available">Available</option>
                <option value="contact_us">Contact Us</option>
                <option value="temporarily_unavailable">Temporarily Unavailable</option>
              </select>
            </label>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">Sales Mode</span>
            <select
              value={form.sales_mode}
              onChange={set("sales_mode")}
              className="mt-1 w-full bg-[#141414] border border-[#262626] rounded-lg px-3 py-2 text-sm text-white focus:border-[#FF6600] outline-none"
              data-testid="software-sales-mode-select"
            >
              <option value="request_quote">Request Pricing (only operational mode this phase)</option>
              <option value="online_purchase">Online Purchase (reserved — CTA still routes to request flow)</option>
              <option value="automated_fulfillment">Automated Fulfillment (reserved — CTA still routes to request flow)</option>
            </select>
            <span className="mt-1 block text-[11px] text-[#7A7A7A]">
              Only <em>Request Pricing</em> has real functionality right now.
              The other two modes exist in the data model for future
              activation — the customer flow safely routes them to the same
              request form with a soft notice.
            </span>
          </label>
        </Section>

        {/* Copy */}
        <Section
          title="Copy"
          destination="Renders on: /software card (tagline + short description) and /software/[slug] hero (long description)"
          hint="Confident, outcome-focused voice — not generic software-store copy."
        >
          <TextField label="Tagline (small bold line on card)" value={form.tagline} onChange={set("tagline")} placeholder="Complete creative toolkit" />
          <TextField label="Short Description (one-liner on card)" value={form.description_short} onChange={set("description_short")} placeholder="Photoshop, Illustrator, InDesign, Premiere Pro and more." textarea rows={2} />
          <TextField label="Long Description (detail page hero)" value={form.description_long} onChange={set("description_long")} textarea rows={4} />
          <div>
            <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">
              "Best For" chips
            </span>
            <div className="mt-1 text-[11px] text-[#7A7A7A]">
              Rendered as compact pill chips on the detail page (between
              Product Highlights and the configurator). Short persona labels
              only — 2-4 words each, 3-5 chips per product. Empty = the
              section is hidden.
            </div>
            <div className="mt-2 space-y-2">
              {(form.best_for || []).map((persona, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={persona}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        best_for: f.best_for.map((p, idx) => (idx === i ? e.target.value : p)),
                      }))
                    }
                    placeholder="e.g. Architects"
                    className="flex-1 bg-[#0F0F0F] border border-[#262626] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
                  />
                  <IconBtn
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        best_for: f.best_for.filter((_, idx) => idx !== i),
                      }))
                    }
                    danger
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </IconBtn>
                </div>
              ))}
              <button
                type="button"
                onClick={() =>
                  setForm((f) => ({ ...f, best_for: [...(f.best_for || []), ""] }))
                }
                className="text-xs text-[#FF6600] font-semibold hover:underline"
              >
                + Add persona
              </button>
            </div>
          </div>
        </Section>

        {/* Product Highlights */}
        <Section
          title="Product Highlights"
          destination="Renders on: /software/[slug] page → section directly under the hero"
          hint={'Editable section heading — use "What\'s Included" for suites, "Key Features" / "Key Capabilities" for individual apps. Populate conservatively (5-6 well-known items); Calvin can expand later.'}
        >
          <TextField
            label="Section Heading"
            value={form.highlights_heading}
            onChange={set("highlights_heading")}
            placeholder="What's Included / Key Features / Key Capabilities"
          />
          <div>
            <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">
              Highlights
            </span>
            <div className="mt-1 text-[11px] text-[#7A7A7A]">
              Rendered as a bulleted grid on the detail page. Leave the whole
              list empty (or blank the heading) to hide the section entirely.
            </div>
            <div className="mt-2 space-y-2" data-testid="software-highlights-editor">
              {(form.highlights || []).map((item, i) => (
                <div key={i} className="flex items-center gap-2">
                  <input
                    value={item}
                    data-testid={`software-highlight-input-${i}`}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        highlights: f.highlights.map((h, idx) =>
                          idx === i ? e.target.value : h,
                        ),
                      }))
                    }
                    placeholder='e.g. "Photoshop" or "Real-time rendering"'
                    className="flex-1 bg-[#0F0F0F] border border-[#262626] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
                  />
                  <IconBtn
                    onClick={() =>
                      setForm((f) => ({
                        ...f,
                        highlights: f.highlights.filter((_, idx) => idx !== i),
                      }))
                    }
                    danger
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </IconBtn>
                </div>
              ))}
              <button
                type="button"
                data-testid="software-add-highlight"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    highlights: [...(f.highlights || []), ""],
                  }))
                }
                className="text-xs text-[#FF6600] font-semibold hover:underline"
              >
                + Add highlight
              </button>
            </div>
          </div>
        </Section>

        {/* Images */}
        <Section
          title="Logo"
          destination="Renders on: /software card + /software/[slug] hero"
          hint="Use the same sourcing approach as the existing publisher icons — do not fabricate brand logos."
        >
          <ImageUploadField
            label="Logo"
            value={form.logo}
            onChange={(v) => setForm((f) => ({ ...f, logo: v }))}
            testId="software-upload-logo"
          />
        </Section>

        {/* Pricing */}
        <Section
          title="Pricing"
          destination="Renders on: /software card price line + /software/[slug] summary panel"
          hint='Leave price at 0 to render "Pricing available on request" instead of a number. Kept off by default — populate as real supplier pricing lands.'
        >
          <div className="grid sm:grid-cols-3 gap-4">
            <TextField label="Price (base)" type="number" value={form.price} onChange={set("price")} />
            <TextField label="Price Label" value={form.price_label} onChange={set("price_label")} placeholder="per year / per user per year / one-time" />
            <TextField label="Currency" value={form.price_currency} onChange={set("price_currency")} placeholder="AWG" />
          </div>
        </Section>

        {/* Option groups */}
        <Section
          title="Option Groups"
          destination="Renders on: /software/[slug] configurator (each group becomes a numbered step)"
          hint="Fully flexible per product. Adobe has Plan/Term/Users; Microsoft 365 has Plan/Seats. Reorder groups with the arrows; each group's first option is default-selected."
        >
          <div className="space-y-5">
            {form.option_groups.map((g, gi) => (
              <div
                key={g.id}
                data-testid={`software-group-row-${gi}`}
                className="rounded-2xl border border-[#262626] bg-[#141414] p-4 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <div className="text-xs uppercase tracking-widest text-[#FF6600] font-bold">
                    Group #{gi + 1}
                  </div>
                  <div className="flex items-center gap-1">
                    <IconBtn onClick={() => moveGroup(gi, -1)} disabled={gi === 0}>
                      <ArrowUp className="w-4 h-4" />
                    </IconBtn>
                    <IconBtn
                      onClick={() => moveGroup(gi, 1)}
                      disabled={gi === form.option_groups.length - 1}
                    >
                      <ArrowDown className="w-4 h-4" />
                    </IconBtn>
                    <IconBtn onClick={() => removeGroup(gi)} danger>
                      <Trash2 className="w-4 h-4" />
                    </IconBtn>
                  </div>
                </div>
                <TextField label="Group Label" value={g.label} onChange={(e) => setGroup(gi, { label: e.target.value })} placeholder="Choose Your Plan" />
                <TextField label="Hint (optional)" value={g.hint} onChange={(e) => setGroup(gi, { hint: e.target.value })} placeholder="Small caption under the group heading" />
                <div>
                  <div className="text-xs uppercase tracking-widest text-[#A3A3A3] mb-2">Options</div>
                  <div className="space-y-2">
                    {g.options.map((o, oi) => (
                      <div key={o.id} className="rounded-lg border border-[#1F1F1F] p-3 space-y-2">
                        <div className="grid sm:grid-cols-[2fr_1fr_1fr_auto] gap-2">
                          <input
                            value={o.label}
                            onChange={(e) => setOption(gi, oi, { label: e.target.value })}
                            placeholder="Option label"
                            className="bg-[#0F0F0F] border border-[#262626] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
                          />
                          <input
                            value={o.price_delta}
                            onChange={(e) => setOption(gi, oi, { price_delta: Number(e.target.value || 0) })}
                            placeholder="Price delta"
                            type="number"
                            className="bg-[#0F0F0F] border border-[#262626] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
                          />
                          <input
                            value={o.badge}
                            onChange={(e) => setOption(gi, oi, { badge: e.target.value })}
                            placeholder='Badge e.g. "Best Value"'
                            className="bg-[#0F0F0F] border border-[#262626] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
                          />
                          <IconBtn onClick={() => removeOption(gi, oi)} danger>
                            <Trash2 className="w-3.5 h-3.5" />
                          </IconBtn>
                        </div>
                        {/* Feb 2026 — per-option decision-clarity fields.
                            Both optional; empty renders nothing on the
                            software detail page. */}
                        <textarea
                          value={o.description || ""}
                          onChange={(e) => setOption(gi, oi, { description: e.target.value })}
                          placeholder="Option description (one sentence explaining the meaningful difference — leave blank if the label is self-explanatory)"
                          rows={2}
                          data-testid={`software-option-description-input-${o.id}`}
                          className="w-full bg-[#0F0F0F] border border-[#262626] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600] resize-y"
                        />
                        <input
                          value={o.best_for || ""}
                          onChange={(e) => setOption(gi, oi, { best_for: e.target.value })}
                          placeholder='Best for / helper text (e.g. "most workstation users")'
                          data-testid={`software-option-best-for-input-${o.id}`}
                          className="w-full bg-[#0F0F0F] border border-[#262626] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
                        />
                      </div>
                    ))}
                    <button
                      onClick={() => addOption(gi)}
                      className="text-xs text-[#FF6600] font-semibold hover:underline"
                    >
                      + Add option
                    </button>
                  </div>
                </div>
              </div>
            ))}
            <button
              data-testid="software-add-group"
              onClick={addGroup}
              className="ur-btn-secondary text-xs py-2 px-3"
            >
              <Plus className="w-3.5 h-3.5" /> Add Option Group
            </button>
          </div>
        </Section>

        {/* Vendor + install */}
        <Section
          title="Vendor Links"
          destination="Stored on the record. Fields exist for future use — NOT surfaced to customers this phase."
          hint="Populate now so the data is ready when the online-purchase / provisioning flows go live."
        >
          <TextField label="Vendor Download / Access URL" value={form.vendor_url} onChange={set("vendor_url")} placeholder="https://www.adobe.com/creativecloud.html" />
          <TextField label="Installation Guide" value={form.install_guide} onChange={set("install_guide")} textarea rows={4} placeholder="Internal notes / installer steps for your team." />
        </Section>

        {/* Add-on services */}
        <Section
          title="Optional Add-on Services"
          destination="Renders on: /software/[slug] page → descriptive shelf below the contact form"
          hint="Simple descriptive links (e.g. Installation & Setup) referencing existing Ur Designs services. No new booking logic — the label is shown, the URL is optional."
        >
          <div className="space-y-2">
            {form.addon_services.map((a, i) => (
              <div key={i} className="grid sm:grid-cols-[2fr_2fr_auto] gap-2">
                <input
                  value={a.label}
                  onChange={(e) => updateAddon(i, { label: e.target.value })}
                  placeholder='Label — e.g. "Installation & Setup"'
                  className="bg-[#0F0F0F] border border-[#262626] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
                />
                <input
                  value={a.url}
                  onChange={(e) => updateAddon(i, { url: e.target.value })}
                  placeholder="/services/setup or full URL"
                  className="bg-[#0F0F0F] border border-[#262626] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#FF6600]"
                />
                <IconBtn onClick={() => removeAddon(i)} danger>
                  <Trash2 className="w-3.5 h-3.5" />
                </IconBtn>
              </div>
            ))}
            <button onClick={addAddon} className="ur-btn-secondary text-xs py-2 px-3">
              <Plus className="w-3.5 h-3.5" /> Add Add-on Service
            </button>
          </div>
        </Section>

        <div className="mt-8 sticky bottom-0 -mx-6 px-6 py-4 bg-[#0F0F0F] border-t border-[#262626]">
          <button
            data-testid="software-save-bottom"
            onClick={commit}
            className="ur-btn-primary w-full"
          >
            Save Product <Save className="w-4 h-4" />
          </button>
        </div>
      </aside>
    </div>
  );
};
